import React from "react";
import { CASE_STUDIES, SECTOR_DEPTH } from "../data/content";
import {
  Reveal,
  Tag,
  SectionNum,
  CredentialPill,
} from "../components/ui-primitives";
import ClosingCTA from "../components/ClosingCTA";
import VerticalTabs from "../components/VerticalTabs";

// Stable id per sector (kebab-case of name, ascii-only).
function sectorId(name) {
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// Panel rendered inside the SECTORS VerticalTabs. Bullets + Brand Spotlight,
// preserved from the legacy SectorCard layout.
function SectorPanel({ sector }) {
  const sid = sectorId(sector.name);
  return (
    <article data-testid={`sector-panel-${sid}`}>
      <ul
        data-testid={`sector-panel-${sid}-bullets`}
        style={{ listStyle: "none", padding: 0, margin: 0 }}
        className="space-y-3"
      >
        {sector.bullets.map((b) => (
          <li
            key={b}
            className="flex items-start gap-3 body-md"
            style={{ color: "var(--color-on-surface)" }}
          >
            <span
              aria-hidden
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "var(--color-tertiary)",
                display: "inline-block",
                flexShrink: 0,
                marginTop: 10,
              }}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {sector.brands && sector.brands.length > 0 && (
        <div
          data-testid={`sector-panel-${sid}-brands`}
          style={{
            marginTop: 24,
            paddingTop: 18,
            borderTop: "1px solid var(--color-outline)",
          }}
        >
          <div
            className="label-md inline-flex items-center gap-2"
            style={{ color: "var(--color-tertiary)", marginBottom: 10 }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--color-tertiary)",
                display: "inline-block",
              }}
            />
            Brand Spotlight
          </div>
          <p
            className="body-md"
            style={{
              color: "var(--color-on-surface)",
              margin: 0,
              lineHeight: 1.7,
            }}
          >
            {sector.brands.map((name, i) => (
              <React.Fragment key={name}>
                {i > 0 && (
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: "var(--color-tertiary)",
                      margin: "0 10px 3px 10px",
                      verticalAlign: "middle",
                    }}
                  />
                )}
                {name}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}
    </article>
  );
}

export default function WorkPage() {
  return (
    <main data-testid="work-page" style={{ background: "var(--color-surface)" }}>
      {/* HERO */}
      <section style={{ paddingTop: 152, paddingBottom: 64 }}>
        <div className="di-container">
          <Tag>Our Work</Tag>
          <h1 className="display-lg mt-6" style={{ maxWidth: 1100 }}>
            Seventeen years.
            <br />
            Brands across every sector.
          </h1>
          <p
            className="mt-8"
            style={{ color: "var(--color-on-surface-variant)", maxWidth: 720 }}
          >
            Across marketing, advertising, and design, we connect diverse
            Indian brands to their audience — at rates no single brand can
            negotiate alone.
          </p>
        </div>
      </section>

      {/* SECTORS WE SERVE — VerticalTabs (left rail = categories, right panel
          = bullets + Brand Spotlight). */}
      <section
        id="sectors"
        data-testid="work-sectors"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <Tag>Sectors</Tag>
          <h2 className="display-md mt-6 mb-10" style={{ maxWidth: 900 }}>
            Deep in every category we serve.
          </h2>

          <div data-testid="sectors-vertical-tabs">
            <VerticalTabs
              testIdPrefix="sectors"
              defaultId={sectorId(SECTOR_DEPTH[0]?.name || "")}
              alignContent="center"
              tabs={SECTOR_DEPTH.map((s) => ({
                id: sectorId(s.name),
                label: s.name,
                render: () => <SectorPanel sector={s} />,
              }))}
            />
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section
        data-testid="work-case-studies"
        className="di-section"
        style={{ background: "var(--color-surface)" }}
      >
        <div className="di-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <Tag>Selected Work</Tag>
              <h2 className="display-md mt-6">
                Campaigns that moved the needle.
              </h2>
            </div>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-12"
            style={{ background: "var(--color-surface-container)", gap: "1px" }}
          >
            {CASE_STUDIES.map((c, i) => (
              <Reveal
                key={c.sector}
                delay={i * 80}
                className={
                  i === 0
                    ? "md:col-span-7"
                    : i === 1
                    ? "md:col-span-5"
                    : "md:col-span-12"
                }
              >
                <div
                  data-testid={`work-case-card-${i + 1}`}
                  style={{
                    background: "var(--color-surface-container-lowest)",
                    padding: "var(--space-48)",
                    height: "100%",
                  }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span
                      className="label-sm"
                      style={{ color: "var(--color-tertiary)" }}
                    >
                      {c.sector}
                    </span>
                    <SectionNum>{String(i + 1).padStart(2, "0")}</SectionNum>
                  </div>
                  <h3
                    className="display-sm"
                    style={{
                      marginBottom: 16,
                      whiteSpace: "pre-line",
                      color: "var(--color-primary)",
                    }}
                  >
                    {c.headline}
                  </h3>
                  <p
                    className="body-md"
                    style={{
                      color: "var(--color-on-surface-variant)",
                      marginBottom: 24,
                    }}
                  >
                    {c.body}
                  </p>
                  <div
                    className="flex flex-wrap gap-2 pt-6"
                    style={{ borderTop: "1px solid var(--color-outline)" }}
                  >
                    {c.stats.map((st) => (
                      <CredentialPill key={st}>{st}</CredentialPill>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </main>
  );
}
